import type { JivuCommand } from './jivu-command.models';

export type JivuCommandReviewTab = 'videos' | 'shorts' | 'images' | 'posts';

export interface JivuCommandReviewRoute {
  pipeline: 'SocialPost';
  tab: JivuCommandReviewTab;
  dateKey?: string;
  previewVideoId?: string;
  previewType?: 'video' | 'shorts';
}

export function completedCommandReviewRoute(command: JivuCommand): JivuCommandReviewRoute | null {
  if (String(command.status || '').toLowerCase() !== 'completed') return null;

  const result = objectValue(command.result);
  const reviewId = stringValue(result['reviewId']) || stringValue(result['itemId']);
  if (!reviewId) return null;

  const driveFileId = stringValue(result['driveFileId'])
    || stringValue(result['videoFileId'])
    || stringValue(result['mediaKey']);
  const inferredTab = reviewTabFromResult(result, driveFileId);
  const tab = inferredTab === 'videos' && command.commandType === 'template_use_render'
    ? 'shorts'
    : inferredTab;
  const dateKey = stringValue(result['dateKey']) || dateKeyFromStorageKey(driveFileId);
  const route: JivuCommandReviewRoute = {
    pipeline: 'SocialPost',
    tab,
    ...(dateKey ? { dateKey } : {})
  };

  if (tab === 'shorts' || tab === 'videos') {
    route.previewVideoId = reviewId;
    route.previewType = tab === 'shorts' ? 'shorts' : 'video';
  }

  return route;
}

export function reviewTabFromResult(result: Record<string, unknown>, driveFileId?: string | null): JivuCommandReviewTab {
  const explicit = stringValue(result['reviewTab']) || stringValue(result['tab']);
  if (explicit === 'shorts' || explicit === 'videos' || explicit === 'images' || explicit === 'posts') {
    return explicit;
  }

  const previewType = stringValue(result['previewType']);
  if (previewType === 'shorts') return 'shorts';

  const normalizedPath = (driveFileId || '').replace(/\\/g, '/').toLowerCase();
  if (normalizedPath.includes('/review/shorts/')) return 'shorts';
  if (normalizedPath.includes('/review/images/')) return 'images';
  if (normalizedPath.includes('/review/posts/')) return 'posts';

  const reviewType = stringValue(result['reviewType']);
  if (reviewType === 'shorts') return 'shorts';
  if (reviewType === 'image') return 'images';
  if (reviewType === 'post') return 'posts';
  return 'videos';
}

export function dateKeyFromStorageKey(value?: string | null): string | null {
  const normalized = (value || '').replace(/\\/g, '/');
  const match = normalized.match(/\/review\/(?:videos|shorts|images|posts)\/(\d{4}-\d{2}-\d{2})\//i);
  return match?.[1] || null;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
